import { Add } from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material"
import { StyledButton } from "components/Button/StyledButton"
import { HaufeOverlay } from "components/HaufeLoader/HaufeLoader"
import { useHaufeLoading } from "contexts/HaufeLoadingContext"
import { CreateContactInput } from "graphql/types"
import { useStoreActions } from "hooks/storeHooks"
import React, { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"

const CreateContactPage: React.FC = () => {
  const navigate = useNavigate()
  const { businessAccountID } = useParams<{ businessAccountID: string }>()
  const { createContact } = useStoreActions((actions) => actions)
  const [formData, setFormData] = useState<Partial<CreateContactInput>>({
    firstName: "",
    lastName: "",
    email: "",
    contactClass: "STANDARD", // Default as per API behavior
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    if (!businessAccountID) {
      setError(
        "Geschäftskonto-ID fehlt in der URL. Kontakt kann nicht erstellt werden."
      )
      // Optionally navigate back or to an error page
      // navigate(-1)
    }
  }, [businessAccountID, navigate])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    })
  }

  const { isHaufeLoading, haufeOperationType } = useHaufeLoading()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setSuccess(null)

    if (!businessAccountID) {
      setError("Kann nicht übermitteln: Geschäftskonto-ID fehlt.")
      setLoading(false)
      return
    }

    if (!formData.firstName || !formData.lastName || !formData.email) {
      setError("Vorname, Nachname und E-Mail sind erforderlich.")
      setLoading(false)
      return
    }

    try {
      // Call the store action to create the contact
      const createdContact = await createContact({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        businessAccountID: businessAccountID,
        contactClass: formData.contactClass || "STANDARD",
      })

      if (createdContact) {
        setSuccess(
          `Kontakt erfolgreich erstellt! Kontakt-ID: ${createdContact.contactID}`
        )
        // Reset form after success
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          contactClass: "",
        })
        // Navigate to the new contact's detail page
        setTimeout(() => {
          navigate(`/data/contacts/${createdContact.contactID}`)
        }, 1500)
      }
    } catch (err: any) {
      setError(err.message || "Kontakt konnte nicht erstellt werden")
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {/* Haufe Overlay for Contact Creation */}
      <HaufeOverlay
        open={
          isHaufeLoading && (haufeOperationType?.includes("Kontakt") || loading)
        }
        message="Kontakt wird in Haufe erstellt..."
        compact={true}
      />

      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Neuen Kontakt erstellen{" "}
          {businessAccountID
            ? `für Geschäftskonto: ${businessAccountID}`
            : ""}
        </Typography>
        {!businessAccountID && error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {businessAccountID && (
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ mt: 1 }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="firstName"
                  label="Vorname"
                  name="firstName"
                  autoComplete="given-name"
                  autoFocus
                  value={formData.firstName}
                  onChange={handleChange}
                  disabled={loading || isHaufeLoading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="lastName"
                  label="Nachname"
                  name="lastName"
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={handleChange}
                  disabled={loading || isHaufeLoading}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="E-Mail-Adresse"
                  name="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading || isHaufeLoading}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  margin="normal"
                  fullWidth
                  id="contactClass"
                  label="Kontaktklasse (z.B. STANDARD)"
                  name="contactClass"
                  value={formData.contactClass}
                  onChange={handleChange}
                  disabled={loading || isHaufeLoading}
                  helperText='Standard ist "STANDARD", wenn leer gelassen.'
                />
              </Grid>
            </Grid>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {success}
              </Alert>
            )}

            <StyledButton
              type="submit"
              disabled={loading || isHaufeLoading}
              sx={{ mt: 3, mb: 2 }}
              startIcon={
                loading || isHaufeLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Add />
                )
              }
            >
              {loading || isHaufeLoading
                ? "Wird erstellt..."
                : "Kontakt erstellen"}
            </StyledButton>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)} // Go back to previous page
              sx={{ mt: 3, mb: 2, ml: 2 }}
              disabled={loading || isHaufeLoading}
            >
              Abbrechen
            </Button>
          </Box>
        )}
        <Snackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSuccess(null)}
            severity="success"
            sx={{ width: "100%" }}
          >
            {success}
          </Alert>
        </Snackbar>
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setError(null)}
            severity="error"
            sx={{ width: "100%" }}
          >
            {error}
          </Alert>
        </Snackbar>
      </Container>
    </>
  )
}

export default CreateContactPage
